/**
 * @fileoverview The "Plant now" button on a Planning plant's bar (E5): one tap records that the plant was planted
 * today, no dialog; an Undo toast takes it back (owner, 2026-10-25: "Not a dialog. Scratch the dialog. But give them an
 * undo.").
 *
 * The stage is the server's choice (the plants brief's plantNow, by the plant's planting window method: sow outside
 * and start indoors record Seeding, plant out records Transplanting); the app sends it to the same call Record a stage
 * makes (POST /stage-marks), so Seasons, Today and the card all update from the one stage history. A plant in the Seed
 * vault is asked "Which bed did they go into?" first (D69, the same sheet Record a stage opens). The flow is in
 * lib/plantNow; this draws the button and the toasts.
 *
 * Where: the plant card's Planning note (PlantCard, in NoWeekNote's children). Only a plant at Planning gets one: the
 * brief sends plantNow for no other stage, and without it nothing is drawn.
 * API: POST /stage-marks/:plantObjId { stageId, date, locationId? } and DELETE /stage-marks/:plantObjId/:markId
 * (api/stageMarks); then the plant is read again (updateSinglePlant).
 * A vault plant's Undo is put back in the Seed vault by the server (it stored that the mark moved the plant, D69); reading the
 * plant again reads the beds again (PlantsContext updateSinglePlant), so this needs no beds context, which the /plants routes
 * do not mount. The toast does not wait for that read: it comes as soon as the server has answered.
 * Words (en/es): plants.card.plantNow.button, done, undo, undone.
 * Failures: one on our side (a 500 or no answer) is reported to Sentry and says the standard sentence
 * (lib/serverFailure); a refusal keeps plants.stage.failed.
 *
 * @module PlantNowButton
 */

import React from 'react';
import { Button } from '@/shared/components/ui/button';
import { ToastAction } from '@/shared/components/ui/toast';
import { useToast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { useStagesContext } from '@/shared/hooks/useStagesContext';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { addStageMark, removeStageMark } from '@/shared/api/stageMarks';
import { serverFailureText } from '@/shared/lib/serverFailure';
import { plantName } from '@/shared/lib/plantName';
import { askWhichBed } from '@/features/seed-orders/lib/whichBed';
import type { Plant } from '@/shared/types/plant';
import { plantNowFlow, type PlantNow } from '../../lib/plantNow';

/**
 * How long the Undo toast stays up, milliseconds: as long as the other undos (garden UNDO_TOAST_MS, the plant list's 10 seconds).
 * @kept K5
 */
const UNDO_TOAST_MS = 10_000;

/** What the button is given. */
interface PlantNowButtonProps {
  /** The plant, at Planning. */
  plant: Plant;
  /** The server's stage for it (the plants brief's plantNow). */
  plantNow: PlantNow;
}

/**
 * The button, and the Undo toast after it.
 *
 * @param props - PlantNowButtonProps.
 */
export const PlantNowButton: React.FC<PlantNowButtonProps> = ({ plant, plantNow }) => {
  const { reactTranslate: t } = useTranslation();
  const { toast } = useToast();
  const { updateSinglePlant } = usePlantsContext();
  const { stages } = useStagesContext();
  /** The garden's own day, the server's ('' until it is known). */
  const { date: today } = useGardenPostcode();
  const [busy, setBusy] = React.useState(false);
  /** Tapped and not undone: the button is gone until the card reads the stage (no second stage can be recorded). */
  const [placed, setPlaced] = React.useState(false);
  /** The stage as the gardener names it. */
  const stageName = stages?.find((stage) => stage.stage_id === plantNow.stageId)?.name ?? null;

  /**
   * Takes the stage back off the plant and reads it again (the server may have put a vault plant back in the Seed vault, D69;
   * reading the plant again reads the beds again too, PlantsContext updateSinglePlant).
   *
   * @param undo - The flow's undo (the server puts a vault plant back in the Seed vault when the mark moved it, D69).
   */
  const takeBack = async (undo: () => Promise<void>): Promise<void> => {
    try {
      await undo();
      setPlaced(false);
      toast({ title: t('plants.card.plantNow.undone') });
      void updateSinglePlant(String(plant.plant_obj_id));
    } catch (error) {
      toast({ title: serverFailureText(error, 'plantNow.undo') ?? t('plants.stage.failed'), variant: 'destructive' });
    }
  };

  /** One tap: records the stage today and offers the Undo. */
  const tap = async (): Promise<void> => {
    if (busy || !today) return;
    setBusy(true);
    try {
      const result = await plantNowFlow(plant, plantNow, today, stageName, { addMark: addStageMark, removeMark: removeStageMark, askBed: askWhichBed });
      if (!result.recorded) return;
      // The server has answered: say so now. The plant (and so the card's bar) is read again behind the toast; the stage the bar
      // and pill show is the server's, from that read, never worked out here.
      setPlaced(true);
      void updateSinglePlant(String(plant.plant_obj_id));
      toast({
        title: t('plants.card.plantNow.done', { plant: plantName(plant), stage: stageName ?? '' }),
        duration: UNDO_TOAST_MS,
        action: (
          <ToastAction altText={t('plants.card.plantNow.undo')} onClick={() => { void takeBack(result.undo); }}>
            {t('plants.card.plantNow.undo')}
          </ToastAction>
        ),
      });
    } catch (error) {
      toast({ title: serverFailureText(error, 'plantNow.record') ?? t('plants.stage.failed'), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  if (placed) return null;
  return (
    <Button type="button" size="sm" className="min-h-11 shrink-0 rounded-xl px-4" disabled={busy || !today} onClick={() => { void tap(); }}>
      {t('plants.card.plantNow.button')}
    </Button>
  );
};
